<template>
  <v-dialog v-model="bulkUpdateDialog" persistent width="1400px">
    <v-card>
      <v-card-title>
        <span class="text-h5">Bulk Update Applications</span>
      </v-card-title>
      <v-card-text>
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-expansion-panels v-model="expansionPanel">
                <v-expansion-panel class="transparent">
                  <v-expansion-panel-header>
                    <v-alert type="info" outlined dense class="mb-0">
                      You are about to update {{ selectedFormItems.length }} application(s)
                    </v-alert>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-list dense>
                      <v-list-item v-for="item in selectedFormItems" :key="item.id">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle>ID: {{ item.id }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
            <v-col cols="12">
              <v-divider />
            </v-col>
            <v-col cols="12">
              <v-card class="mt-4 elevation-2">
                <v-tabs v-model="tabs" slider-color="accent">
                  <v-tab data-cy="parameters-tab">Parameters</v-tab>
                  <v-tab data-cy="sdk-configurations-tab">
                    SDK Configurations
                  </v-tab>
                  <v-tab data-cy="configuration-tab">Configuration</v-tab>
                  <v-tab v-if="attributesTabEnabled" data-cy="attributes-tab">Attributes</v-tab>
                </v-tabs>
                <v-tabs-items v-model="tabs">
                  <v-tab-item data-cy="parameters-pane">
                    <parameters-bulk-update
                      v-if="selectedFormItems.length"
                      ref="parameters"
                      :is-editable="canEdit"
                    />
                  </v-tab-item>
                  <v-tab-item data-cy="sdk-configurations-pane">
                    <s-d-k-configurations-bulk-update
                      v-if="selectedFormItems.length"
                      ref="sdkConfigurations"
                      :is-editable="canEdit"
                    />
                  </v-tab-item>
                  <v-tab-item data-cy="configuration-pane">
                    <configuration-flags-bulk-update
                      v-if="selectedFormItems.length"
                      ref="configurationFlags"
                      :is-editable="canEdit"
                    />
                  </v-tab-item>
                  <v-tab-item v-if="attributesTabEnabled" data-cy="attributes-pane">
                    <attributes-bulk-update
                      v-if="selectedFormItems.length"
                      ref="attributes"
                      :is-editable="canEdit"
                    />
                  </v-tab-item>
                </v-tabs-items>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <span class="text-body-2">Note: Update will apply changes from all tabs</span>
        <v-spacer />
        <v-btn text @click="closeBulkUpdateDialog">
          Cancel
        </v-btn>
        <v-btn color="secondary" :loading="bulkUpdateLoading" @click="performBulkUpdate">
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import HTTP from '@/api/dog';
import ParametersBulkUpdate from './ParametersBulkUpdate.vue';
import SDKConfigurationsBulkUpdate from './SDKConfigurationsBulkUpdate.vue';
import ConfigurationFlagsBulkUpdate from './ConfigurationFlagsBulkUpdate.vue';
import AttributesBulkUpdate from './AttributesBulkUpdate.vue';

export default {
  name: 'BulkUpdateForm',
  components: {
    ParametersBulkUpdate,
    SDKConfigurationsBulkUpdate,
    ConfigurationFlagsBulkUpdate,
    AttributesBulkUpdate,
  },
  props: {
    selectedItems: {
      type: Array,
      default: () => [],
    },
    dialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      bulkUpdateLoading: false,
      tabs: 0,
      canEdit: true,
      expansionPanel: 0,
    };
  },
  computed: {
    bulkUpdateDialog: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit('update:dialog', value);
      },
    },
    selectedFormItems() {
      return this.selectedItems;
    },
    attributesTabEnabled() {
      return this.$auth.hasPermissions('dogbulk.updateattributes');
    },
  },
  methods: {
    closeBulkUpdateDialog() {
      this.bulkUpdateDialog = false;
    },
    async performBulkUpdate() {
      this.bulkUpdateLoading = true;
      try {
        const applicationIds = this.selectedFormItems.map(item => item.id);

        const parametersData = this.$refs.parameters?.getData() || {};
        const parameters = (parametersData.parameters || []).map(p => ({
          master_parameter_id: p.master_parameter_id,
          value: p.value,
          encoding: p.encoding,
          status: p.status,
        }));

        const sdkConfigData = this.$refs.sdkConfigurations?.getData() || {};
        const sdk_configurations = (sdkConfigData.sdk_configurations || []).map(s => ({
          name: s.name,
          value: s.value,
          description: s.description,
        }));

        let configuration = {};
        try {
          configuration = { ...(this.$refs.configurationFlags?.getData() || {}) };
        } catch (error) {
          console.error('Failed to get configuration data:', error);
        }

        let attributes = {};
        if (this.attributesTabEnabled) {
          try {
            attributes = this.$refs.attributes?.getData() || {};
          } catch (error) {
            console.error('Failed to get attributes data:', error);
          }
        }

        const updateData = {
          application_ids: applicationIds,
        };

        if (parameters.length > 0) {
          updateData.parameters = parameters;
        }

        if (sdk_configurations.length > 0) {
          updateData.sdk_configurations = sdk_configurations;
        }

        if (Object.keys(configuration).length > 0) {
          updateData.configuration = configuration;
        }

        if (Object.keys(attributes).length > 0) {
          updateData.attributes = attributes;
        }

        await HTTP.post('/applications/bulk_update', updateData);

        this.$flash('success', `Successfully updated application(s)`);
        this.$emit('saved');
        this.closeBulkUpdateDialog();
      } catch (error) {
        this.$emit('error', error.response?.data?.message || 'Failed to update applications');
      } finally {
        this.bulkUpdateLoading = false;
      }
    },
  },
};
</script>
